import { Box, Button, Divider, Fade, Flex, Hide, Show, Text } from "@chakra-ui/react"
import { getCookie, setCookie } from "cookies-next"
import { useRouter } from "next/router"
import { useState } from "react"
import RichTextLabel from "../../../../src/components/shared/RichTextLabel"
import { lastSeenCookie } from "../../../common/constants/browser-storage"
import { cookieSolutionServices } from "../../../common/constants/constants"
import { campaignTypes } from "../../../common/constants/search-constants"
import { useGetLastSeen } from "../../../common/hooks/queries"
import { useBrandedTranslation } from "../../../common/hooks/useBrandedTranslation"
import useConsentStatus from "../../../common/hooks/useConsentStatus"
import { handleUcSecondLayerClick } from "../../../common/utils/cookie-solution-utils"
import { getCampaignItems, getSortedItems } from "../../../common/utils/search-utils"
import { AlertBox } from "../../shared/AlertBox"
import { CarouselTiles } from "../../shared/CarouselTiles"
import { CInfo, CTrash } from "../../shared/Icons"
import Loader from "../../shared/Loader"
import { SectionTitle } from "../../shared/SectionTitle"

const LastSeenItems = () => {
  const { t } = useBrandedTranslation()
  const {
    locale,
    query: { brand },
  } = useRouter()
  const lastSeen = String(getCookie(lastSeenCookie.KEY) ?? "")
  const [hasItems, setHasItems] = useState(!!lastSeen)

  const lastSeenConsented = useConsentStatus(cookieSolutionServices.GEBERIT_COMFORT)

  const resetLastSeen = () => {
    setCookie(lastSeenCookie.KEY, "", { maxAge: lastSeenCookie.MAX_AGE })
    setHasItems(false)
  }

  const { data } = useGetLastSeen(locale as string, String(brand), lastSeen)
  const campaignItems = getCampaignItems(data, campaignTypes.HOME_LAST_SEEN)
  const sortedItems = getSortedItems(campaignItems, lastSeen.split(lastSeenCookie.DELIMITER))

  return (
    <Box as="section">
      <Fade in={true} unmountOnExit>
        <Divider mt={{ base: "10", sm: "16", md: "20" }} mb={{ base: "12", md: "16" }} />
        <Box>
          <Flex justify="space-between" align="center" mb="6">
            <SectionTitle title={t("carousel-last-seen-title")} mb="0" />
            {hasItems && lastSeenConsented && lastSeen && (
              <Show above="xs">
                <Button
                  leftIcon={<CTrash w="4" h="5" />}
                  iconSpacing="2.5"
                  variant="secondary"
                  color="black"
                  onClick={resetLastSeen}
                >
                  <Text as="span" fontSize="sm" fontWeight="medium">
                    {t("carousel-last-seen-reset")}
                  </Text>
                </Button>
              </Show>
            )}
          </Flex>
          {hasItems && lastSeenConsented && lastSeen ? (
            <>
              {data ? (
                <>
                  <CarouselTiles items={sortedItems} />
                  <Hide above="xs">
                    <Button
                      leftIcon={<CTrash w="4" h="5" />}
                      iconSpacing="3"
                      onClick={resetLastSeen}
                      mt="2"
                      w="100%"
                      variant="secondary"
                      color="black"
                      border="1px solid"
                    >
                      <Text as="span">{t("carousel-last-seen-reset")}</Text>
                    </Button>
                  </Hide>
                </>
              ) : (
                <Loader mt={{ base: "10", sm: "16", md: "20" }} />
              )}
            </>
          ) : (
            <>
              {lastSeenConsented ? (
                <AlertBox colorSide="brand.500" colorMain="brand.50" icon={<CInfo w="6" h="6" color="white" />}>
                  <Text fontSize="sm">{t("last-seen-empty")}</Text>
                </AlertBox>
              ) : (
                <AlertBox colorSide="brand.500" colorMain="brand.50" icon={<CInfo w="6" h="6" color="white" />}>
                  <Box onClick={handleUcSecondLayerClick}>
                    <RichTextLabel
                      i18nKey="cookie-last-seen_rtf"
                      styleProps={{
                        sx: {
                          a: {
                            color: "brand.500",
                            fontWeight: "medium",
                            _hover: { textDecoration: "underline" },
                            _active: { color: "brand.700" },
                          },
                        },
                        fontSize: "sm",
                      }}
                    />
                  </Box>
                </AlertBox>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Box>
  )
}

export default LastSeenItems
